import * as types from '../mutation-types';
import CommunicationConnectionAPI from '../../api/communicationConnection';

const state = {
  record: {},
  testResult: null,
  accounts: [],
  health: null,
  summary: null,
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isDeleting: false,
    isTestingConnection: false,
    isFetchingAccounts: false,
    isBinding: false,
    isRebinding: false,
    isFetchingHealth: false,
    isFetchingSummary: false,
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getCommunicationConnection($state) {
    return $state.record;
  },
  getTestResult($state) {
    return $state.testResult;
  },
  getAccounts($state) {
    return $state.accounts;
  },
  getHealth($state) {
    return $state.health;
  },
  getSummary($state) {
    return $state.summary;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await CommunicationConnectionAPI.get();
      commit(types.default.SET_COMMUNICATION_CONNECTION, response.data);
    } catch (error) {
      commit(types.default.SET_COMMUNICATION_CONNECTION, {});
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isFetching: false,
      });
    }
  },

  create: async ({ commit }, params) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isUpdating: true,
    });
    try {
      const response = await CommunicationConnectionAPI.create(params);
      commit(types.default.SET_COMMUNICATION_CONNECTION, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isUpdating: false,
      });
    }
  },

  update: async ({ commit }, params) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isUpdating: true,
    });
    try {
      const response = await CommunicationConnectionAPI.update(params);
      commit(types.default.SET_COMMUNICATION_CONNECTION, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isUpdating: false,
      });
    }
  },

  delete: async ({ commit }) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isDeleting: true,
    });
    try {
      await CommunicationConnectionAPI.delete();
      commit(types.default.SET_COMMUNICATION_CONNECTION, {});
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isDeleting: false,
      });
    }
  },

  testConnection: async ({ commit }) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isTestingConnection: true,
    });
    try {
      const response = await CommunicationConnectionAPI.testConnection();
      commit(
        types.default.SET_COMMUNICATION_CONNECTION_TEST_RESULT,
        response.data
      );
      return response.data;
    } catch (error) {
      const result = error?.response?.data || {
        reachable: false,
        error: 'Unable to reach the gateway',
      };
      commit(types.default.SET_COMMUNICATION_CONNECTION_TEST_RESULT, result);
      return result;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isTestingConnection: false,
      });
    }
  },

  fetchAccounts: async ({ commit }) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isFetchingAccounts: true,
    });
    try {
      const response = await CommunicationConnectionAPI.fetchAccounts();
      commit(
        types.default.SET_COMMUNICATION_CONNECTION_ACCOUNTS,
        response.data
      );
      return response.data;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isFetchingAccounts: false,
      });
    }
  },

  bind: async ({ commit }, params) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isBinding: true,
    });
    try {
      const response = await CommunicationConnectionAPI.bind(params);
      return response.data;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isBinding: false,
      });
    }
  },

  rebind: async ({ commit }, params) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isRebinding: true,
    });
    try {
      const response = await CommunicationConnectionAPI.rebind(params);
      return response.data;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isRebinding: false,
      });
    }
  },

  fetchHealth: async ({ commit }) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isFetchingHealth: true,
    });
    try {
      const response = await CommunicationConnectionAPI.fetchHealth();
      commit(types.default.SET_COMMUNICATION_CONNECTION_HEALTH, response.data);
      return response.data;
    } catch (error) {
      const result = { reachable: false, error: 'Unable to reach the gateway' };
      commit(types.default.SET_COMMUNICATION_CONNECTION_HEALTH, result);
      return result;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isFetchingHealth: false,
      });
    }
  },

  fetchSummary: async ({ commit }) => {
    commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
      isFetchingSummary: true,
    });
    try {
      const response = await CommunicationConnectionAPI.fetchSummary();
      commit(types.default.SET_COMMUNICATION_CONNECTION_SUMMARY, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG, {
        isFetchingSummary: false,
      });
    }
  },
};

export const mutations = {
  [types.default.SET_COMMUNICATION_CONNECTION_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_COMMUNICATION_CONNECTION]($state, data) {
    $state.record = data;
  },
  [types.default.SET_COMMUNICATION_CONNECTION_TEST_RESULT]($state, data) {
    $state.testResult = data;
  },
  [types.default.SET_COMMUNICATION_CONNECTION_ACCOUNTS]($state, data) {
    $state.accounts = data;
  },
  [types.default.SET_COMMUNICATION_CONNECTION_HEALTH]($state, data) {
    $state.health = data;
  },
  [types.default.SET_COMMUNICATION_CONNECTION_SUMMARY]($state, data) {
    $state.summary = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
